"use client";

import { useSubUser } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useSubUser";
import { useMultiUserGTMV2NoConstraints } from "@faire/deprecated-retailer-visitor-shared/hooks/useMultiUserGTMV2";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { CondensedBrandHeaderDropdownListItem } from "@faire/deprecated-retailer-visitor-shared/views/brand/BrandHeader/CondensedBrandHeaderDropdownListItem";
import { LeanInviteTeamModal } from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Invite/LeanInviteTeamModal";
import {
  PageName,
  VIEW_COUNT_BEFORE_SILENCING_GTM,
} from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Share/consts";
import { ShareWithTeamGTMModal } from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Share/ShareWithTeamGTMModal";
import { ShareWithTeamModal } from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Share/ShareWithTeamModal";
import { Core } from "@faire/design-tokens";
import { Button } from "@faire/slate/components/button";
import { Share } from "@faire/slate/icons/Share";
import { trackRetailerTeamManagementShareButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/shareButton";
import { trackRetailerTeamManagementShareButtonView } from "@faire/web-api--source/events/retailerTeamManagement/view/shareButton";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IconButton } from "@faire/web/slate/IconButton";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect } from "react";
import styled from "styled-components";

interface IProps {
  type: ShareableItemType;
  token: string | undefined;
  variant: "tertiary" | "secondary";
  // The page the share button is on for event tracking purposes
  pageName: PageName;
  buttonText?: string;
  image?: IImage;
  name?: string;
  subText?: string;
  isIconButton?: boolean;
  iconSize?: string | number;
  fullWidth?: boolean;
  setModalOpen?: () => void;
  setModalClose?: () => void;
}

enum ModalType {
  NONE = "NONE",
  SHARE_MODAL = "SHARE_MODAL",
  LEAN_INVITE_MODAL = "LEAN_INVITE_MODAL",
  SHARE_MODAL_WITH_GTM = "SHARE_MODAL_WITH_GTM",
}

export const ShareButton: React.FC<IProps> = ({
  type,
  image,
  name,
  subText,
  token,
  variant,
  pageName,
  buttonText,
  isIconButton,
  fullWidth,
  iconSize,
  setModalOpen,
  setModalClose,
}) => {
  const [currentModal, setCurrentModal] = React.useState<ModalType>(
    ModalType.NONE
  );
  const showGTMVersion = useMultiUserGTMV2NoConstraints();

  useEffect(() => {
    if (currentModal !== ModalType.NONE) {
      setModalOpen?.();
    } else {
      setModalClose?.();
    }
  }, [currentModal, setModalOpen, setModalClose]);

  const { hasEverHadActiveSubUser } = useSubUser();

  const ref = useDoActionWhenVisible<HTMLButtonElement>(() => {
    trackRetailerTeamManagementShareButtonView(pageName);
  });
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();

  const handleShareButtonClick = () => {
    if (!hasEverHadActiveSubUser && showGTMVersion) {
      setCurrentModal(ModalType.SHARE_MODAL_WITH_GTM);
    } else {
      setCurrentModal(ModalType.SHARE_MODAL);
    }
    trackRetailerTeamManagementShareButtonClick(pageName);
  };

  if (!hasEverHadActiveSubUser && !showGTMVersion) {
    return null;
  }

  const showCopyLinkButton =
    type === ShareableItemType.PRODUCT || type === ShareableItemType.BRAND;

  const isLoggedInRetailerAndBrand =
    isLoggedInRetailerOrBrandPreview && type === ShareableItemType.BRAND;

  return (
    <>
      {currentModal === ModalType.SHARE_MODAL_WITH_GTM ? (
        <ShareWithTeamGTMModal
          modalType={type}
          image={image}
          name={name}
          subText={subText}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInvite={() => setCurrentModal(ModalType.NONE)}
          pageName={pageName}
          silenceGTMMilestone={MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING}
          viewCountBeforeSilencingGTM={VIEW_COUNT_BEFORE_SILENCING_GTM}
        />
      ) : null}
      {currentModal === ModalType.SHARE_MODAL ? (
        <ShareWithTeamModal
          modalType={type}
          image={image}
          name={name}
          subText={subText}
          token={token}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInviteButtonClick={() =>
            setCurrentModal(ModalType.LEAN_INVITE_MODAL)
          }
          pageName={pageName}
          showCopyLinkButton={showCopyLinkButton}
        />
      ) : null}
      {currentModal === ModalType.LEAN_INVITE_MODAL ? (
        <LeanInviteTeamModal
          onBack={() => setCurrentModal(ModalType.SHARE_MODAL)}
          onClose={() => setCurrentModal(ModalType.NONE)}
          onInvite={() => setCurrentModal(ModalType.NONE)}
          pageName={pageName}
        />
      ) : null}

      {isLoggedInRetailerAndBrand ? (
        <CondensedBrandHeaderDropdownListItem onClick={handleShareButtonClick}>
          <Share fontSize="24px" />
          <Typography variant="paragraphSansRegular">
            <StrictLocalMsg
              id="MTCv0T"
              defaultMessage="Share"
              description={{
                text: "A text copy in a dropdown list to allow user share the brand page link",
                img: "https://cdn.faire.com/fastly/245379bb1db16df727cfe4cc7dd59703e377afbf5d612a546b2a097c7b9fb9b1.png",
              }}
            />
          </Typography>
        </CondensedBrandHeaderDropdownListItem>
      ) : (
        <StyledShareButton
          ref={ref}
          buttonText={buttonText}
          variant={variant}
          isIconButton={isIconButton}
          handleShareButtonClick={handleShareButtonClick}
          iconSize={iconSize}
          fullWidth={fullWidth}
        />
      )}
    </>
  );
};

const StyledShareButton = ({
  buttonText,
  variant,
  isIconButton,
  handleShareButtonClick,
  iconSize,
  fullWidth,
  ref,
}: Pick<
  IProps,
  "buttonText" | "variant" | "isIconButton" | "iconSize" | "fullWidth"
> & {
  handleShareButtonClick: () => void;
  ref: React.Ref<HTMLButtonElement>;
}) => {
  const { strictLocalize } = useStrictLocalization();

  const label = strictLocalize({
    id: "iVSuEm",
    defaultMessage: "Share",
    description: { text: "Button to share with team" },
  });

  if (buttonText) {
    return (
      <Button
        ref={ref}
        onClick={handleShareButtonClick}
        variant={variant}
        aria-label={label}
        data-test-id="share-icon-wrapper"
        iconProps={{
          Component: renderShareIconComponent(iconSize),
          position: "start",
        }}
        fullWidth={fullWidth}
      >
        {buttonText}
      </Button>
    );
  }

  if (isIconButton) {
    return (
      <IconButton
        buttonRef={ref}
        onClick={handleShareButtonClick}
        variant={variant}
        aria-label={label}
        data-test-id="share-icon-wrapper"
      >
        <StyledShareIcon fontSize="22px" />
      </IconButton>
    );
  }

  return (
    <Button
      ref={ref}
      onClick={handleShareButtonClick}
      variant={variant}
      aria-label={label}
      data-test-id="share-icon-wrapper"
      fullWidth={fullWidth}
    >
      <StyledShareIcon fontSize="22px" />
    </Button>
  );
};

const renderShareIconComponent = (iconSize: string | number | undefined) => {
  return function () {
    return <StyledShareIcon fontSize={iconSize ?? "22px"} />;
  };
};

const StyledShareIcon = styled(Share)`
  vertical-align: baseline;
  &:hover {
    color: ${Core.icon.hover};
  }
`;
